import { createInitializerQueueList } from './initializer.manager';
import { EnvironmentProviders, inject, Injector, provideAppInitializer } from '@angular/core';
import { DecoratorService } from '../helpers/decorator-service.service';
import { GlobalEventsService } from '../services/global-events.service';
import { GoogleTagManagerService } from '../services/google-tag-manager.service';
import { PwaService } from '../services/pwa.service';
import { UserPaymentModeService } from '../services/user/user-payment-mode.service';
import { SeoService } from '../services/seo.service';
import { WebSocketService } from '../services/web-socket.service';
import { ThemeService } from '../services/theme.service';
import { ZendeskChatService } from '../services/zendesk/zendesk-chat.service';
import { TelegramAppService } from '../services/telegram-app.service';
import { CheckWebpService } from '../services/check-webp.service';
import { NotificationCenterService } from '../shared/modules/notification-center/notification-center.service';
import { UbidexService } from '../services/ubidex.service';
import {
  CustomNotificationCenterService
} from '../shared/modules/notification-center/custom-notification-center.service';
import {DataPreloaderService} from "../services/data-preloader.service";
import {GameProvidersService} from "../services/games/game-providers.service";
import {ContentUpdaterService} from "../services/content-updater/content-updater.service";
import {UserPhoneService} from "../services/user/user-phone.service";
import {OneSignalService} from "../services/one-signal.service";
import {SocketSuccessHandlerService} from "../services/socket-success-handler.service";
import {FraudCheckService} from "../services/fraud-check.service";
import { UserTransactionsService } from '../services/user/user-transactions.service';
import { XmasService } from '../../page/promo/xmas/xmas.service';
import { HalloweenService } from '../../page/promo/halloween/halloween.service';

/**
 * The `initApp` function initializes the DecoratorService with the provided Injector.
 * @param {Injector} injector - The `injector` parameter in the `initApp` function is of type
 * `Injector`. It is used to provide dependencies to the application components. The `Injector` is a
 * dependency injection container that resolves dependencies for classes and services in Angular
 * applications.
 * @returns A function is being returned.
 */
export function initApp(injector: Injector) {
  DecoratorService.injector = injector;
}

export const provideInitializer = () => {
  return [
    provideAppInitializer(() => {
      const injector = inject(Injector);
      initApp(injector);
      return Promise.resolve();
    }),
    ...parallelAppInitializer,
  ];
};

const parallelAppInitializer: EnvironmentProviders[] = createInitializerQueueList([
  {
    token: DataPreloaderService,
    initializerFn: (service: DataPreloaderService) => service.preloadCriticalData(),
  },
  {
    token: GlobalEventsService,
    initializerFn: (service: GlobalEventsService) => service.initGlobalEvent(),
  },
  {
    token: GoogleTagManagerService,
    initializerFn: (service: GoogleTagManagerService) => service.addGtmToDom(),
  },
  {
    token: PwaService,
    initializerFn: (service: PwaService) => service.registerServiceWorker(),
  },
  {
    token: UserPaymentModeService,
    initializerFn: (service: UserPaymentModeService) => service.initPaymentMode(),
  },
  {
    token: UserPhoneService,
    initializerFn: (service: UserPhoneService) => service.updatePhoneStoreMask(),
  },
  {
    token: SeoService,
    initializerFn: (service: SeoService) => service.updateGlobalHeadLink(),
  },
  {
    token: ThemeService,
    initializerFn: (service: ThemeService) => service.resolveTheme$(),
  },
  {
    token: ContentUpdaterService,
    initializerFn: (service: ContentUpdaterService) => service.runContentChangeHandler(),
  },
  {
    token: GameProvidersService,
    initializerFn: (service: GameProvidersService) => service.updateProviderListOnLangChange(),
  },
  {
    token: ZendeskChatService,
    initializerFn: (service: ZendeskChatService) => service.initChat(),
  },
  {
    token: TelegramAppService,
    initializerFn: (service: TelegramAppService) => service.detectTelegramApp(),
  },
  {
    token: CheckWebpService,
    initializerFn: (service: CheckWebpService) => service.checkIfWebpSupported(),
  },
  {
    token: CustomNotificationCenterService,
    initializerFn: (service: CustomNotificationCenterService) => service.initCustomNotify(),
  },
  {
    token: NotificationCenterService,
    initializerFn: (service: NotificationCenterService) => service.initNotify(),
  },
  {
    token: UbidexService,
    initializerFn: (service: UbidexService) => service.initUbidex(),
  },
  {
    token: OneSignalService,
    initializerFn: (service: OneSignalService) => service.handleId(),
  },
  {
    token: WebSocketService,
    initializerFn: (service: WebSocketService) => service.createConnection(),
  },
  {
    token: SocketSuccessHandlerService,
    initializerFn: (service: SocketSuccessHandlerService) => service.initHandler(),
  },
  {
    token: FraudCheckService,
    initializerFn: (service: FraudCheckService) => service.initFraudCheck(),
  },
  {
    token: UserTransactionsService,
    initializerFn: (service: UserTransactionsService) => service.transactionListAuthed(),
  },
  {
    token: XmasService,
    initializerFn: (service: XmasService) => service.initPromo$(),
  },
  {
    token: HalloweenService,
    initializerFn: (service: HalloweenService) => service.initPromo$(),
  },
]);
